import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./profile-modern.css";
import BackButton from "../Navigation/BackButton";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import AIDocumentUpload from "../Documents/AIDocumentUpload";
import StyledModalView from "../Modal/StyledModalView";
import ImageCropper from "../ImageCropper/ImageCropper";

function ProfileViewModern({
  profile,
  editableProfile,
  selectedFile,
  previewImage,
  isEditing,
  error,
  showConfirmModal,
  setShowConfirmModal,
  handleInputChange,
  handleEditClick,
  handleCancelClick,
  handleSaveClick,
  confirmSave,
  handleFileChange,
  showUploadForm,
  setShowUploadForm,
  baseUrl,
  onDocumentUpload,
  onDeleteUserDocument,
  fetchDocuments,
  file,
  setFile,
  documentCategory,
  setDocumentCategory,
  expires,
  setExpiresAt,
  documents,
  editingDocId,
  editedExpiry,
  setEditedExpiry,
  editedCategory,
  setEditedCategory,
  handleEditExpiry,
  handleSaveExpiry,
  handleCancelEditExpiry,
  // Image cropper props
  showImageCropper,
  imageToCrop,
  handleCropComplete,
  handleCropCancel,
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const fileInputRef = useRef();

  if (!profile) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={t('common.error')}/>;

  // Helper function to normalize document category for comparison
  const normalizeDocCategory = (category) => {
    if (!category) return '';
    return category.toLowerCase().replace(/[\s\-_]/g, '');
  };

  const getDocumentStatus = () => {
    const requiredTypes = [
      { type: 'vca diploma', normalized: 'vcadiploma' },
      { type: 'kvk-uittreksel', normalized: 'kvkuittreksel' },
      { type: 'verzekeringspolis', normalized: 'verzekeringspolis' }
    ];
    const now = new Date();
    let hasMissing = false;
    let hasExpired = false;

    for (const req of requiredTypes) {
      const docs = documents.filter(doc =>
        normalizeDocCategory(doc.document_category) === req.normalized
      );
      if (docs.length === 0) {
        hasMissing = true;
        continue;
      }
      const hasValid = docs.some(doc => {
        const exp = doc.expires_at ? new Date(doc.expires_at) : null;
        return !exp || exp >= now;
      });
      if (!hasValid) hasExpired = true;
    }

    if (hasMissing) return "missing";
    if (hasExpired) return "expired";
    return "valid";
  };

  const documentStatus = getDocumentStatus();

  const handleAvatarClick = () => {
    if (isEditing) {
      // In edit mode, trigger file input
      fileInputRef.current?.click();
    } else if (previewImage) {
      // Not editing, show modal
      setShowImageModal(true);
    }
  };

  const handleDeleteClick = (docId) => {
    setDocToDelete(docId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (docToDelete) {
      await onDeleteUserDocument(docToDelete);
      setShowDeleteConfirm(false);
      setDocToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDocToDelete(null);
  };

  return (
    <div className="profile-detail-modern">
      <BackButton label={t('nav.dashboard')} />

      {/* Modern Header */}
      <div className="profile-header">
        <div className="profile-header-main">
          <div className="profile-avatar" onClick={handleAvatarClick}>
            {previewImage ? (
              <img src={previewImage} alt={profile.name} className="profile-avatar-img" />
            ) : (
              <div className="profile-avatar-placeholder">
                <span aria-hidden="true">👤</span>
              </div>
            )}
            {isEditing && (
              <div className="avatar-upload-overlay">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="avatar-upload-input"
                  style={{ display: 'none' }}
                />
                <span className="avatar-upload-text">📷 {t('profile.changePhoto')}</span>
              </div>
            )}
          </div>

          <div className="profile-header-info">
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={editableProfile.name}
                onChange={handleInputChange}
                className="profile-name-input"
              />
            ) : (
              <h2>{profile.name}</h2>
            )}

            <div className="profile-meta">
              <span className="profile-role">
                <span aria-hidden="true">💼</span> {profile.role?.charAt(0).toUpperCase() + profile.role?.slice(1)}
              </span>
              <span className={`profile-status ${profile.is_active ? 'active' : 'inactive'}`}>
                <span aria-hidden="true">{profile.is_active ? '✓' : '✕'}</span>
                {profile.is_active ? t('profile.active') : t('profile.inactive')}
              </span>
              <span className={`doc-status ${documentStatus}`}>
                <span aria-hidden="true">📄</span>
                {documentStatus === 'valid' ? t('documents.valid') :
                 documentStatus === 'expired' ? t('documents.expired') :
                 t('documents.missing')}
              </span>
            </div>

            {selectedFile && (
              <div className="upload-indicator">
                ✓ {t('profile.newPhotoSelected')} ({(selectedFile.size / 1024).toFixed(0)}KB)
              </div>
            )}
          </div>
        </div>

        <div className="profile-header-actions">
          {!isEditing ? (
            <button className="btn btn-edit" onClick={handleEditClick}>
              {t('common.edit')}
            </button>
          ) : (
            <>
              <button className="btn btn-save" onClick={handleSaveClick}>
                {t('common.save')}
              </button>
              <button className="btn btn-cancel" onClick={handleCancelClick}>
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && previewImage && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <button className="image-modal-close" onClick={() => setShowImageModal(false)}>
            ✕
          </button>
          <img src={previewImage} alt={profile.name} className="image-modal-img" />
        </div>
      )}

      {/* Desktop Tabs */}
      <div className="tabs-container desktop-tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          {t('profile.userInfo')}
        </button>
        <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>
          {t('documents.title')} <span className="tab-count">({documents?.length || 0})</span>
        </button>
      </div>

      {/* Mobile Tab Selector */}
      <select
        className="tab-selector mobile-tabs"
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value)}
        aria-label={t('common.selectTab')}
      >
        <option value="info">{t('profile.userInfo')}</option>
        <option value="documents">{t('documents.title')} ({documents?.length || 0})</option>
      </select>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-grid">
            <div className="info-card">
              <h3>{t('profile.personalInfo')}</h3>
              <div className="info-rows">
                <div className="info-row">
                  <label>{t('common.email')}:</label>
                  {isEditing ? (
                    <input name="email" value={editableProfile.email} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">
                      {profile.email ? <a href={`mailto:${profile.email}`}>{profile.email}</a> : '-'}
                    </span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('common.phone')}:</label>
                  {isEditing ? (
                    <input name="phone" value={editableProfile.phone} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">
                      {profile.phone ? <a href={`tel:${profile.phone}`}>{profile.phone}</a> : '-'}
                    </span>
                  )}
                </div>
                <div className="info-row">
                  <label>{t('common.address')}:</label>
                  {isEditing ? (
                    <input name="address" value={editableProfile.address} onChange={handleInputChange} className="form-input" />
                  ) : (
                    <span className="info-value">{profile.address || '-'}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>{t('profile.companyInfo')}</h3>
              <div className="info-rows">
                <div className="info-row">
                  <label>{t('profile.companyName')}:</label>
                  <span className="info-value">{profile.company_name || '-'}</span>
                </div>
                <div className="info-row">
                  <label>{t('profile.kvkNumber')}:</label>
                  <span className="info-value">{profile.kvk_number || '-'}</span>
                </div>
                <div className="info-row">
                  <label>{t('profile.vatNumber')}:</label>
                  <span className="info-value">{profile.vat_number || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="documents-section">
            <button className="btn btn-add mb-4" onClick={() => setShowUploadForm(!showUploadForm)}>
              {showUploadForm ? t('common.cancel') : t('documents.uploadDocument')}
            </button>

            {showUploadForm && (
              <div className="upload-card mb-6">
                <AIDocumentUpload
                  userId={profile.id}
                  expectedDocumentType={null}
                  onValidationComplete={(result) => {
                    console.log('AI Validation:', result);
                  }}
                  onUploadSuccess={(data) => {
                    console.log('Upload success:', data);
                    setShowUploadForm(false);
                    // Refresh documents list without page reload
                    if (fetchDocuments) {
                      fetchDocuments();
                    }
                  }}
                />
              </div>
            )}

            {documents && documents.length > 0 ? (
              <div className="documents-grid">
                {documents.map((doc) => {
                  const isExpired = doc.expires_at && new Date(doc.expires_at) < new Date();
                  const isEditing = editingDocId === doc.id;
                  return (
                    <div key={doc.id} className="document-card">
                      <div className="document-header">
                        {isEditing ? (
                          <select
                            value={editedCategory}
                            onChange={(e) => setEditedCategory(e.target.value)}
                            className="form-select-small"
                          >
                            <option value="KvK-uittreksel">KvK-uittreksel</option>
                            <option value="VCA-diploma">VCA-diploma</option>
                            <option value="Verzekeringspolis">Verzekeringspolis</option>
                            <option value="VOG">VOG</option>
                            <option value="ID-bewijs">ID-bewijs</option>
                            <option value="Contract">Contract</option>
                            <option value="Overig">Overig</option>
                          </select>
                        ) : (
                          <h4>{doc.document_category}</h4>
                        )}
                        <button
                          className="btn-icon btn-delete-small"
                          onClick={() => handleDeleteClick(doc.id)}
                          aria-label={t('common.delete')}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="document-info">
                        <p className="document-filename">{doc.file_name}</p>
                        <p className={`document-expiry ${isExpired ? 'expired' : ''}`}>
                          {isEditing ? (
                            <input
                              type="date"
                              value={editedExpiry}
                              onChange={(e) => setEditedExpiry(e.target.value)}
                              className="form-input-small"
                            />
                          ) : (
                            <>
                              📅 {doc.expires_at ? new Date(doc.expires_at).toLocaleDateString('nl-NL') : t('documents.notSet') || 'Niet ingesteld'} {isExpired && '⚠️'}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="document-actions">
                        <a
                          href={`${baseUrl}/uploads/users/${profile.id}/${encodeURIComponent(doc.file_name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-small btn-view"
                        >
                          {t('common.view')}
                        </a>
                        {isEditing ? (
                          <>
                            <button className="btn-small btn-save" onClick={() => handleSaveExpiry(doc.id)}>💾</button>
                            <button className="btn-small btn-cancel" onClick={handleCancelEditExpiry}>✕</button>
                          </>
                        ) : (
                          <button className="btn-small btn-edit" onClick={() => handleEditExpiry(doc)}>{t('common.edit')}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <NoDataConstruction text={t('documents.noDocuments')} />
            )}
          </div>
        )}
      </div>

      {/* Confirm Save Modal */}
      <StyledModalView
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t('profile.confirmSave') || 'Bevestig opslaan'}
        confirmText={t('common.save') || 'Opslaan'}
        closeText={t('common.cancel') || 'Annuleren'}
        onConfirm={confirmSave}
      >
        <p>{t('profile.confirmSaveMessage') || 'Weet je zeker dat je deze wijzigingen wilt opslaan?'}</p>
      </StyledModalView>

      {/* Confirm Delete Document Modal */}
      <StyledModalView
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        title={t('documents.confirmDelete') || 'Document verwijderen'}
        confirmText={t('common.delete') || 'Verwijderen'}
        closeText={t('common.cancel') || 'Annuleren'}
        onConfirm={confirmDelete}
      >
        <p>{t('documents.confirmDeleteMessage') || 'Weet je zeker dat je dit document wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.'}</p>
      </StyledModalView>

      {/* Image Cropper Modal */}
      {showImageCropper && imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

ProfileViewModern.propTypes = {
  profile: PropTypes.object,
  editableProfile: PropTypes.object.isRequired,
  selectedFile: PropTypes.object,
  previewImage: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
  error: PropTypes.string,
  showConfirmModal: PropTypes.bool,
  setShowConfirmModal: PropTypes.func,
  handleInputChange: PropTypes.func.isRequired,
  handleEditClick: PropTypes.func.isRequired,
  handleCancelClick: PropTypes.func.isRequired,
  handleSaveClick: PropTypes.func.isRequired,
  confirmSave: PropTypes.func.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  showUploadForm: PropTypes.bool.isRequired,
  setShowUploadForm: PropTypes.func.isRequired,
  baseUrl: PropTypes.string.isRequired,
  onDocumentUpload: PropTypes.func.isRequired,
  onDeleteUserDocument: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func,
  file: PropTypes.object,
  setFile: PropTypes.func.isRequired,
  documentCategory: PropTypes.string.isRequired,
  setDocumentCategory: PropTypes.func.isRequired,
  expires: PropTypes.string.isRequired,
  setExpiresAt: PropTypes.func.isRequired,
  documents: PropTypes.array,
  editingDocId: PropTypes.number,
  editedExpiry: PropTypes.string,
  setEditedExpiry: PropTypes.func.isRequired,
  editedCategory: PropTypes.string,
  setEditedCategory: PropTypes.func.isRequired,
  handleEditExpiry: PropTypes.func.isRequired,
  handleSaveExpiry: PropTypes.func.isRequired,
  handleCancelEditExpiry: PropTypes.func.isRequired,
  // Image cropper
  showImageCropper: PropTypes.bool,
  imageToCrop: PropTypes.string,
  handleCropComplete: PropTypes.func.isRequired,
  handleCropCancel: PropTypes.func.isRequired,
};

export default ProfileViewModern;


